import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { walletAPI, depositAPI } from '../../utils/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowDownCircle, Copy } from 'lucide-react';

const DepositCrypto = () => {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState('');
  const [amount, setAmount] = useState('');
  const [depositAddress, setDepositAddress] = useState('');

  useEffect(() => {
    fetchWallets();
  }, []);

  const fetchWallets = async () => {
    try {
      const response = await walletAPI.getAll();
      setWallets(response.data.filter(w => w.asset_symbol !== 'USD'));
    } catch (error) {
      toast.error('Failed to load wallets');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAddress = async (e) => {
    e.preventDefault();
    try {
      const response = await depositAPI.request({
        asset_symbol: selectedAsset,
        amount: parseFloat(amount),
        type: 'crypto'
      });
      if (response.data.wallet_address) {
        setDepositAddress(response.data.wallet_address);
        toast.success('Deposit address generated!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate deposit address');
    }
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(depositAddress);
    toast.success('Address copied to clipboard!');
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="loading-spinner"></div></div>;

  const cryptoWallets = wallets.filter(w => w.asset_symbol !== 'USD');

  return (
    <div className="space-y-8 animate-fadeIn">
      <div>
        <h1 className="text-4xl font-bold text-black mb-2">Deposit Crypto</h1>
        <p className="text-gray-600">Deposit cryptocurrency to your account</p>
      </div>

      <div className="max-w-2xl">
        <Card className="p-6">
          <h2 className="text-xl font-bold text-black mb-4 flex items-center">
            <ArrowDownCircle className="w-5 h-5 mr-2 text-[#0F4C5C]" />
            Generate Deposit Address
          </h2>

          <form onSubmit={handleGenerateAddress} className="space-y-4">
            <div>
              <Label className="text-black">Select Cryptocurrency</Label>
              <Select value={selectedAsset} onValueChange={setSelectedAsset}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose asset" />
                </SelectTrigger>
                <SelectContent>
                  {cryptoWallets.map(wallet => (
                    <SelectItem key={wallet.asset_symbol} value={wallet.asset_symbol}>
                      {wallet.asset_symbol} - Balance: {wallet.balance.toFixed(4)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-black">Amount (optional)</Label>
              <Input
                type="number"
                step="0.00000001"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mt-1"
                placeholder="Enter amount"
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty to generate address for any amount</p>
            </div>

            <Button type="submit" className="w-full text-white hover:opacity-90" style={{ background: '#062136' }}>
              Generate Deposit Address
            </Button>
          </form>

          {depositAddress && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <Label className="text-black mb-2 block">Deposit Address</Label>
              <div className="flex items-center space-x-2">
                <Input
                  value={depositAddress}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  size="icon"
                  variant="outline"
                  onClick={copyAddress}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-600 mt-2">
                Send {selectedAsset} to this address. Minimum confirmations: 3
              </p>
            </div>
          )}
        </Card>

        <Card className="p-6 mt-6">
          <h3 className="font-semibold text-black mb-3">Important Notes</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start">
              <span className="text-[#FF5A5A] mr-2">•</span>
              <span>Only send {selectedAsset || 'the selected cryptocurrency'} to this address</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#FF5A5A] mr-2">•</span>
              <span>Deposits require network confirmations before being credited</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#FF5A5A] mr-2">•</span>
              <span>Double-check the address before sending funds</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#FF5A5A] mr-2">•</span>
              <span>Contact support if you don't see your deposit after confirmations</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default DepositCrypto;
